import { apiCall, type ApiResponse } from './apiClient';
import type { VendorRecord } from '../types/pipeline';

export interface MarketResearchDd2579CoordinationProduct {
  product_id: string;
  product_name: string;
  selected_vendors: VendorRecord[];
  selection_signature: string;
  rationale?: string | null;
  requires_rationale: boolean;
  verification_required: boolean;
}

export interface MarketResearchDd2579Coordination {
  products: MarketResearchDd2579CoordinationProduct[];
  unknown_product_ids: string[];
  requires_coordination: boolean;
  dd2579_eligible: boolean;
  /** Draft navigation only; does not authorize solicitation issuance. */
  can_continue_to_synopsis?: boolean;
  dd2579_continue_missing_fields?: string[];
  dd2579_continue_form_current?: boolean;
  applicability?: {
    status: 'applicable' | 'exempt' | 'unresolved';
    basis: string;
    reason_codes: string[];
    missing_fields: string[];
    context_fingerprint: string;
    policy_version: string;
    official_references: Array<{ title: string; url?: string; section?: string; provenance?: string }>;
  };
  acquisition_context?: MarketResearchAcquisitionContext;
  coordination_status?: 'not_required' | 'pending' | 'recorded' | 'stale' | 'needs_revision';
  coordination_record_summary?: {
    recording_mode: 'in_stage_confirmation' | 'external_signed_record';
    review_record_id?: string;
    recorded_at?: string;
    actor?: { id: string; role: string };
    signed_record_reference?: string;
    co_action: 'approved' | 'rejected';
  } | null;
  can_record_coordination?: boolean;
  can_edit_acquisition_policy_context?: boolean;
  review_token?: string;
  next_stage?: 11 | 12 | null;
}

export interface MarketResearchAcquisitionContext {
  schema_version: 1;
  agency: 'DAF' | 'DoD' | 'unknown';
  acquisition_category?: 'supplies' | 'services' | 'construction' | 'unknown';
  local_profile?: 'services_rfq_2026_07_08';
  local_services_mfr?: { authority: string; reference: string };
  total_estimated_value: string | null;
  value_includes_options: boolean | null;
  threshold_basis: 'standard_usd' | 'labor_services' | 'construction' | 'special' | 'unknown';
  procedure: 'simplified' | 'sealed_bid' | 'negotiated' | 'schedule_order' | 'other_order' | 'unknown';
  action: 'new' | 'order' | 'modification' | 'unknown';
  set_aside: 'total_small_business' | 'partial' | 'none' | 'unknown';
  scope_change?: boolean | null;
  funding_only?: boolean | null;
  agency_rule?: { review_required: boolean | null; authority: string; reference: string } | null;
  documented_exception?: { code: string; authority: string; reference: string } | null;
}

export async function saveMarketResearchAcquisitionContext(
  flowId: string,
  acquisitionContext: MarketResearchAcquisitionContext,
): Promise<ApiResponse<MarketResearchDd2579Coordination>> {
  return apiCall('/api/market-research/dd2579/acquisition-context', 'POST', {
    flow_id: flowId, acquisition_context: acquisitionContext,
  }, { retries: 0 });
}

export async function recordMarketResearchCoordination(
  flowId: string,
  record: Record<string, unknown>,
  expectedReviewToken: string,
): Promise<ApiResponse<MarketResearchDd2579Coordination>> {
  return apiCall('/api/market-research/dd2579/coordination/record', 'POST', {
    flow_id: flowId, record, expected_review_token: expectedReviewToken,
  }, { retries: 0 });
}

export interface MarketResearchVendorSelection {
  product_id: string;
  vendor_ids: string[];
}

/**
 * Store the browser's Stage-6 selections, then obtain the server-derived
 * coordination decision. Vendor details and small-business assertions are
 * intentionally never trusted from this payload.
 */
export async function saveMarketResearchDd2579Selections(
  flowId: string,
  selections: MarketResearchVendorSelection[],
): Promise<ApiResponse<MarketResearchDd2579Coordination>> {
  return apiCall<MarketResearchDd2579Coordination>(
    '/api/market-research/dd2579/coordination/selections',
    'POST',
    { flow_id: flowId, selections },
    { retries: 0 },
  );
}

export async function getMarketResearchDd2579Coordination(
  flowId: string,
): Promise<ApiResponse<MarketResearchDd2579Coordination>> {
  return apiCall<MarketResearchDd2579Coordination>(
    `/api/market-research/dd2579/coordination?flow_id=${encodeURIComponent(flowId)}`,
    'GET',
    undefined,
    { retries: 0 },
  );
}

export async function saveMarketResearchDd2579Rationale(
  flowId: string,
  productId: string,
  rationale: string,
  selectionSignature: string,
): Promise<ApiResponse<MarketResearchDd2579Coordination>> {
  return apiCall<MarketResearchDd2579Coordination>(
    '/api/market-research/dd2579/rationales',
    'POST',
    {
      flow_id: flowId,
      product_id: productId,
      rationale,
      selection_signature: selectionSignature,
    },
    { retries: 0 },
  );
}
